// Workshop service for KATA Dashboard

import { apiClient, handleApiError } from './api';
import { Workshop, WorkshopDetail, ApiResponse, DashboardStats } from '../types/api';

export interface WorkshopFilters {
  search?: string;
  alv_id?: string;
  status?: 'closed' | 'rejected' | 'fully_implemented' | 'in_progress' | 'on_hold' | 'risk_of_delay' | 'new' | 'na';
  statuses?: string[]; // Support for multiple statuses
  overdue?: boolean; // Support for overdue workshops filtering
  department?: number;
  progress_min?: number;
  progress_max?: number;
  deadline_from?: string;
  deadline_to?: string;
  mentor?: string;
  ordering?: string;
  page?: number;
  page_size?: number;
}

export interface WorkshopCreateData {
  workshop_number?: string;
  title: string;
  status: 'closed' | 'rejected' | 'fully_implemented' | 'in_progress' | 'on_hold' | 'risk_of_delay' | 'new' | 'na';
  progress_rate?: number;
  startup_date?: string;
  deadline?: string;
  mentor?: number;
  estimated_savings_per_year?: number;
  estimated_savings_unit?: 'EUR' | 'KEUR' | 'MEUR';
  department?: number;
  sqdcm_category?: string;
  improvement_description?: string;
  initial_condition?: number;
  target_condition?: number;
  // Team fields
  mentees?: number[];
  kpi?: string;
  indicator_frequency?: 'daily' | 'weekly' | 'monthly';
  current_condition?: number;
  released_savings?: number;
  // New fields from Excel analysis
  product_line?: string;
  alv_id?: string;
  location?: string;
  category?: string;
  ebit_bridge_lever?: string;
  impact_type?: string;
  workstream?: string;
  aps_maturity?: string;
  ws_area?: string;
  l_gate?: string;
  priority?: string;
  description?: string;
}

export interface WorkshopUpdateData extends Partial<WorkshopCreateData> {}

export interface ProgressUpdateData {
  progress_rate: number;
  current_condition?: number;
  status?: 'closed' | 'rejected' | 'fully_implemented' | 'in_progress' | 'on_hold' | 'risk_of_delay' | 'new' | 'na';
}

export class WorkshopService {
  /**
   * Format workshop number for display
   */
  static formatWorkshopNumber(workshopNumber: string | number | undefined | null): string {
    // Handle null/undefined cases
    if (workshopNumber === null || workshopNumber === undefined) {
      return 'Loading...';
    }
    
    if (typeof workshopNumber === 'number') {
      return `WS-${workshopNumber.toString().padStart(4, '0')}`;
    }
    
    // If it's already a string, check if it needs formatting
    if (typeof workshopNumber === 'string' && workshopNumber.trim() !== '') {
      if (!workshopNumber.includes('WS-')) {
        const num = parseInt(workshopNumber);
        if (!isNaN(num)) {
          return `WS-${num.toString().padStart(4, '0')}`;
        }
      }
      return workshopNumber;
    }
    
    // Fallback for empty strings or other edge cases
    return 'Loading...';
  }

  /**
   * Get workshops with filtering and pagination
   */

  /**
   * Get available plants (ALV IDs) based on current filters
   */
  static async getAvailablePlants(filters?: { deadline_from?: string; deadline_to?: string }): Promise<Array<{value: string, label: string}>> {
    try {
      const params = new URLSearchParams();
      if (filters?.deadline_from) params.append('deadline_from', filters.deadline_from);
      if (filters?.deadline_to) params.append('deadline_to', filters.deadline_to);
      
      const response = await apiClient.get(`/workshops/available_plants/?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  static async getWorkshops(filters: WorkshopFilters = {}): Promise<ApiResponse<Workshop>> {
    try {
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (key === 'statuses' && Array.isArray(value)) {
            // Handle multiple statuses by joining them with commas
            params.append(key, value.join(','));
          } else {
            params.append(key, value.toString());
          }
        }
      });

      const response = await apiClient.get<ApiResponse<Workshop>>(`/workshops/?${params}`);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get workshop by ID
   */
  static async getWorkshop(id: number): Promise<WorkshopDetail> {
    try {
      const response = await apiClient.get<WorkshopDetail>(`/workshops/${id}/`);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Create new workshop
   */
  static async createWorkshop(data: WorkshopCreateData): Promise<WorkshopDetail> {
    try {
      const response = await apiClient.post<WorkshopDetail>('/workshops/', data);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Update workshop
   */
  static async updateWorkshop(id: number, data: WorkshopUpdateData): Promise<WorkshopDetail> {
    try {
      const response = await apiClient.patch<WorkshopDetail>(`/workshops/${id}/`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Delete workshop
   */
  static async deleteWorkshop(id: number): Promise<void> {
    try {
      await apiClient.delete(`/workshops/${id}/`);
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Update workshop progress
   */
  static async updateProgress(id: number, data: ProgressUpdateData): Promise<WorkshopDetail> {
    try {
      const response = await apiClient.patch<WorkshopDetail>(`/workshops/${id}/update_progress/`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Update a single field of a workshop
   */
  static async updateField(id: number, fieldName: string, value: any): Promise<WorkshopDetail> {
    try {
      const data = { [fieldName]: value };
      const response = await apiClient.patch<WorkshopDetail>(`/workshops/${id}/`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get workshop dashboard statistics
   */
  static async getDashboardStats(): Promise<any> {
    try {
      const response = await apiClient.get('/dashboard/stats/');
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get overdue workshops
   */
  static async getOverdueWorkshops(): Promise<{ count: number; workshops: Workshop[] }> {
    try {
      const response = await apiClient.get('/workshops/overdue/');
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get workshops with upcoming deadlines
   */
  static async getUpcomingDeadlines(): Promise<{ count: number; workshops: Workshop[] }> {
    try {
      const response = await apiClient.get('/workshops/upcoming_deadlines/');
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get workshops grouped by department
   */
  static async getWorkshopsByDepartment(): Promise<any[]> {
    try {
      const response = await apiClient.get('/workshops/by_department/');
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Bulk update workshop progress (admin only)
   */
  static async bulkUpdateProgress(updates: Array<{ id: number; progress_rate: number }>): Promise<{ updated_count: number; errors: string[] }> {
    try {
      const response = await apiClient.post('/workshops/bulk_update_progress/', { workshops: updates });
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Format progress rate for display
   */
  static formatProgress(progress: number | null): string {
    if (progress === null || progress === undefined) return 'N/A';
    return `${Math.round(progress)}%`;
  }

  /**
   * Format status for display
   */
  static formatStatus(status: string): string {
    switch (status) {
      case 'fully_implemented':
        return 'Fully implemented';
      case 'in_progress':
        return 'In progress';
      case 'on_hold':
        return 'On hold';
      case 'risk_of_delay':
        return 'Risk of delay';
      case 'closed':
        return 'Closed';
      case 'rejected':
        return 'Rejected';
      case 'new':
        return 'New';
      case 'na':
        return 'NA';
      default:
        return status?.charAt(0).toUpperCase() + status?.slice(1) || 'Unknown';
    }
  }

  /**
   * Get status badge color
   */
  static getStatusColor(status: string): string {
    switch (status) {
      case 'fully_implemented':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'new':
        return 'bg-indigo-100 text-indigo-800';
      case 'on_hold':
        return 'bg-yellow-100 text-yellow-800';
      case 'risk_of_delay':
        return 'bg-orange-100 text-orange-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'na':
        return 'bg-gray-50 text-gray-600';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  /**
   * Get progress status color
   */
  static getProgressColor(progress: number | null): string {
    if (progress === null || progress === undefined) return 'bg-gray-200';
    if (progress >= 100) return 'bg-green-500';
    if (progress >= 75) return 'bg-yellow-500';
    if (progress >= 50) return 'bg-blue-500';
    if (progress >= 25) return 'bg-orange-500';
    return 'bg-red-500';
  }

  /**
   * Format currency values
   */
  static formatCurrency(value: string | number | null): string {
    if (!value) return 'N/A';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return 'N/A';
    
    if (numValue >= 1000000) {
      return `${(numValue / 1000000).toFixed(1)}M€`;
    } else if (numValue >= 1000) {
      return `${(numValue / 1000).toFixed(0)}K€`;
    } else {
      return `${numValue.toFixed(0)}€`;
    }
  }

  /**
   * Format currency values with their specific unit
   */
  static formatCurrencyWithUnit(value: string | number | null, unit: 'EUR' | 'KEUR' | 'MEUR'): string {
    if (!value) return 'N/A';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return 'N/A';
    
    const unitDisplay = {
      'EUR': '€',
      'KEUR': 'K€', 
      'MEUR': 'M€'
    };
    
    // Format with appropriate decimal places
    const formattedValue = numValue % 1 === 0 ? numValue.toFixed(0) : numValue.toFixed(1);
    return `${formattedValue}${unitDisplay[unit]}`;
  }

  /**
   * Format date for display
   */
  static formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid Date';
    }
  }

  /**
   * Calculate days until deadline
   */
  static getDaysUntilDeadline(deadline: string | null): { days: number; isOverdue: boolean } | null {
    if (!deadline) return null;
    
    try {
      const deadlineDate = new Date(deadline);
      const today = new Date();
      const diffTime = deadlineDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return {
        days: Math.abs(diffDays),
        isOverdue: diffDays < 0
      };
    } catch {
      return null;
    }
  }
}
